import { BuildingOfficeIcon, BanknotesIcon, UserIcon, MapPinIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, List, ListItem } from "@material-tailwind/react";
import Label from "components/Label/Label";
import { LocationFormProps } from "data/reduxInterfaces";
import { useAppSelector } from "hooks/hooks";
import { FC, HTMLAttributes, useEffect, useState } from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import Input from "shared/Input/Input";
import PhoneInput, { phoneSchema } from "shared/Phone/PhoneInput";
import OnboardingTour, { stepStyle } from "utils/tours/OnboardingTour";
import { z } from "zod";

const formSchema = z.object({
  category: z.string(),
  email: z.string().email({ message: "Veuillez entrer une adresse email valide." }),
  companyName: z.string({
    required_error: "Le nom de l'entreprise est obligatoire.",
    invalid_type_error: "Le nom de l'entreprise est obligatoire.",
  }),
  owner_title: z.string({
    required_error: "Le poste du propriétaire est obligatoire.",
    invalid_type_error: "Le poste du propriétaire est obligatoire.",
  }),
  companySiren: z.string({
    required_error: "Le SIREN de l'entreprise est obligatoire.",
    invalid_type_error: "Le SIREN de l'entreprise est obligatoire.",
  }),
  bank: z.string({
    required_error: "Le compte bancaire est obligatoire.",
    invalid_type_error: "Le compte bancaire est obligatoire.",
  }),
  rib: z
    .string()
    .min(27, { message: "Le RIB doit contenir exactement 27 caractères." })
    .max(27, { message: "Le RIB doit contenir exactement 27 caractères." })
    .refine((value) => value.startsWith("FR"), {
      message: "Le RIB doit commencer par 'FR'.",
    }),
  city: z.string().min(1, { message: "La ville est obligatoire." }),
  address: z.string().min(1, { message: "L'adresse est obligatoire." }),
  address2: z.string().optional(),
  country: z.string().min(1, { message: "Le pays est obligatoire." }),
  codePostale: z.number({
    required_error: "Le code postal est obligatoire.",
    invalid_type_error: "Le code postal doit être un nombre.",
  }),
  stripe_url: z.string().optional(),
  companyPhone: phoneSchema,
  owner_city: z.string().min(1, { message: "La ville est obligatoire." }),
  owner_address: z.string().min(1, { message: "L'adresse est obligatoire." }),
  owner_address2: z.string().optional(),
  owner_country: z.string().min(1, { message: "Le pays est obligatoire." }),
  owner_postal_code: z.number({
    required_error: "Le code postal est obligatoire.",
    invalid_type_error: "Le code postal doit être un nombre.",
  }),
});

const LocationCompanyPersonalDataModern: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  handleForm,
  data,
  errorLocationForm,
  FormData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    control,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: "company",
      city: data?.city ?? "",
      address: data?.address ?? "",
      address2: data?.address2 ?? "",
      country: data?.country ?? "",
      codePostale: data?.codePostale ?? undefined,
      owner_city: data?.owner_city ?? "",
      owner_address: data?.owner_address ?? "",
      owner_address2: data?.owner_address ?? "",
      owner_country: data?.owner_country ?? "",
      owner_postal_code: data?.owner_postal_code ?? undefined,
      stripe_url: "eventtobee.com",
    },
    mode: "onSubmit",
  });

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  useEffect(() => {
    if (handleForm.type === "chekLocationFormFourteenCompany") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const updatelocationStates = useAppSelector((state) => state.location.locationData);
  useEffect(() => {
    if (updatelocationStates) {
      setValue("companyName", updatelocationStates.companyName);
      setValue("companySiren", updatelocationStates.companySiren);
      setValue("bank", updatelocationStates.bank);
      setValue("rib", updatelocationStates.rib);
      setValue("stripe_url", updatelocationStates.stripe_url);
    }
  }, [updatelocationStates]);

  const { placesService, placePredictions, isPlacePredictionsLoading, getPlacePredictions } = usePlacesService({
    apiKey: process.env.REACT_APP_MAPS_API_KEY + "&loading=async",
    options: {
      input: "",
      componentRestrictions: {
        country: "fr",
      },
    },
  });

  useEffect(() => {
    if (data) {
      setValue("city", data.city);
      setValue("address", data.address);
      setValue("address2", data.address2);
      setValue("codePostale", data.codePostale);
      setValue("owner_city", data.city);
      setValue("owner_address", data.address);
      setValue("owner_address2", data.address2);
      setValue("owner_postal_code", data.codePostale);
    }
  }, [data]);

  const [isload, setIload] = useState(isLoading);
  const [isOwnerload, setOwnerload] = useState(isLoading);

  const holdeSelectAddress = async (place_id: string) => {
    setIload(true);
    placesService?.getDetails({ placeId: place_id }, (placeDetails: any, placeStatus: any) => {
      if (placeStatus !== "OK") {
        toast.error("Quelque chose ne fonctionne pas, veuillez réessayer!");
      }
      const parser = new DOMParser();
      const adr_address = parser.parseFromString(placeDetails?.adr_address ?? "", "text/html");
      const streetAddress = adr_address.querySelector(".street-address")?.textContent || "";
      const postalCode = adr_address.querySelector(".postal-code")?.textContent || "";
      const locality = adr_address.querySelector(".locality")?.textContent || "";
      const country_name = adr_address.querySelector(".country-name")?.textContent || "";

      if (streetAddress !== placeDetails?.name) {
        setValue("address", `${placeDetails?.name}, ${streetAddress}`);
      } else {
        setValue("address", streetAddress);
      }
      setValue("codePostale", Number(postalCode));
      setValue("city", locality);
      setValue("country", country_name);

      setIload(false);
    });

    getPlacePredictions({ input: "" });
  };

  const holdeSelectOwnerAddress = async (place_id: string) => {
    setOwnerload(true);
    placesService?.getDetails({ placeId: place_id }, (placeDetails: any, placeStatus: any) => {
      if (placeStatus !== "OK") {
        toast.error("Quelque chose ne fonctionne pas, veuillez réessayer!");
      }
      const parser = new DOMParser();
      const adr_address = parser.parseFromString(placeDetails?.adr_address ?? "", "text/html");
      const streetAddress = adr_address.querySelector(".street-address")?.textContent || "";
      const postalCode = adr_address.querySelector(".postal-code")?.textContent || "";
      const locality = adr_address.querySelector(".locality")?.textContent || "";
      const country_name = adr_address.querySelector(".country-name")?.textContent || "";

      if (streetAddress !== placeDetails?.name) {
        setValue("owner_address", `${placeDetails?.name}, ${streetAddress}`);
      } else {
        setValue("owner_address", streetAddress);
      }
      setValue("owner_postal_code", Number(postalCode));
      setValue("owner_city", locality);
      setValue("owner_country", country_name);

      setOwnerload(false);
    });

    getPlacePredictions({ input: "" });
  };

  const steps = [
    {
      selector: ".image",
      content: "Une image est une représentation visuelle.",
      stepStyle,
    },
    {
      selector: ".nom-entreprise",
      content:
        "Le nom commercial est le nom de l'entreprise perçu par le public. Il permet d'identifier le fonds de commerce ainsi que l'activité exercée par une entreprise",
    },
    {
      selector: ".num-siren ",
      content: "Le numéro SIREN (pour est le numéro unique d'identification de chaque entreprise. ",
    },
    {
      selector: ".num-identite ",
      content:
        "Toute personne est identifiée dès sa naissance par son numéro d'inscription au répertoire national d'identification des personnes physiques. ",
    },
    {
      selector: ".num-telephone ",
      content:
        "Un numéro de téléphone est une séquence unique et nominative de chiffres permettant de vous contacter. ",
    },
  ];

  return (
    <div className={className}>
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">📝 Informations de l'entreprise</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Complétez les informations de votre entreprise et du propriétaire
        </p>
        <div className="border-b-2 w-20 border-eventToBee mt-4 mx-auto"></div>
      </div>

      <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
        {/* Section Informations de l'entreprise */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-6">
            <BuildingOfficeIcon className="w-6 h-6 text-eventToBee mr-3" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Informations de l'entreprise</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="nom-entreprise">
              <Label>Nom de l'entreprise *</Label>
              <Input
                type="text"
                placeholder="Nom de l'entreprise"
                className={`mt-1.5 ${errors.companyName?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                {...register("companyName")}
              />
              {errors.companyName?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.companyName?.message}</div>
              )}
            </div>

            <div className="num-siren">
              <Label>Numéro SIREN *</Label>
              <Input
                type="text"
                placeholder="SIREN"
                className={`mt-1.5 ${errors.companySiren?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                {...register("companySiren")}
              />
              {errors.companySiren?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.companySiren?.message}</div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>E-mail de l'entreprise *</Label>
              <Input
                type="email"
                placeholder="contact@entreprise.fr"
                className={`mt-1.5 ${errors.email?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                {...register("email")}
              />
              {errors.email?.message && <div className="text-xs text-red-500 mt-1">{errors.email?.message}</div>}
            </div>

            <div className="md:col-span-2">
              <Controller
                name="companyPhone"
                control={control}
                render={({ field }) => (
                  <PhoneInput value={field.value} onChange={field.onChange} error={errors.companyPhone?.message} />
                )}
              />
            </div>
          </div>
        </div>

        {/* Section Adresse de l'entreprise */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-6">
            <MapPinIcon className="w-6 h-6 text-eventToBee mr-3" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Adresse de l'entreprise</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label>Adresse *</Label>
              <Input
                className={`mt-1.5 ${errors.address?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Adresse de l'entreprise"
                {...register("address")}
                onChange={(e) => {
                  setValue("address", e.target.value);
                  getPlacePredictions({ input: e.target.value });
                }}
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    getPlacePredictions({ input: "" });
                  }
                }}
                loading={isPlacePredictionsLoading || isload}
              />

              {placePredictions.length > 0 && (
                <div className="relative z-10 mt-1">
                  <Card className="w-full">
                    <List>
                      {placePredictions.map((item, index) => (
                        <ListItem key={index} onClick={() => holdeSelectAddress(item.place_id)}>
                          {item.description}
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </div>
              )}

              {errors.address?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.address?.message}</div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>Complément d'adresse</Label>
              <Input
                disabled={isLoading || isload}
                placeholder="Étage, appartement, etc."
                {...register("address2")}
              />
            </div>

            <div>
              <Label>Ville *</Label>
              <Input
                className={`mt-1.5 ${errors.city?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Ville"
                {...register("city")}
              />
              {errors.city?.message && <div className="text-xs text-red-500 mt-1">{errors.city?.message}</div>}
            </div>

            <div>
              <Label>Code postal *</Label>
              <Input
                className={`mt-1.5 ${errors.codePostale?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="Code postal"
                type="number"
                {...register("codePostale", { valueAsNumber: true })}
              />
              {errors.codePostale?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.codePostale?.message}</div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>Pays *</Label>
              <Input
                className={`mt-1.5 ${errors.country?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isload}
                placeholder="France"
                {...register("country")}
              />
              {errors.country?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.country?.message}</div>
              )}
            </div>
          </div>
        </div>

        {/* Section Propriétaire de l'entreprise */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-6">
            <UserIcon className="w-6 h-6 text-eventToBee mr-3" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Informations du propriétaire</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label>Poste du propriétaire *</Label>
              <Input
                type="text"
                placeholder="PDG, Gérant, etc."
                className={`mt-1.5 ${errors.owner_title?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                {...register("owner_title")}
              />
              {errors.owner_title?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.owner_title?.message}</div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>Adresse du propriétaire *</Label>
              <Input
                className={`mt-1.5 ${errors.owner_address?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isOwnerload}
                placeholder="Adresse du propriétaire"
                {...register("owner_address")}
                onChange={(e) => {
                  setValue("owner_address", e.target.value);
                  getPlacePredictions({ input: e.target.value });
                }}
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    getPlacePredictions({ input: "" });
                  }
                }}
                loading={isPlacePredictionsLoading || isOwnerload}
              />

              {placePredictions.length > 0 && (
                <div className="relative z-10 mt-1">
                  <Card className="w-full">
                    <List>
                      {placePredictions.map((item, index) => (
                        <ListItem key={index} onClick={() => holdeSelectOwnerAddress(item.place_id)}>
                          {item.description}
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </div>
              )}

              {errors.owner_address?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.owner_address?.message}</div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>Complément d'adresse</Label>
              <Input
                disabled={isLoading || isOwnerload}
                placeholder="Étage, appartement, etc."
                {...register("owner_address2")}
              />
            </div>

            <div>
              <Label>Ville *</Label>
              <Input
                className={`mt-1.5 ${errors.owner_city?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isOwnerload}
                placeholder="Ville"
                {...register("owner_city")}
              />
              {errors.owner_city?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.owner_city?.message}</div>
              )}
            </div>

            <div>
              <Label>Code postal *</Label>
              <Input
                className={`mt-1.5 ${errors.owner_postal_code?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isOwnerload}
                placeholder="Code postal"
                type="number"
                {...register("owner_postal_code", { valueAsNumber: true })}
              />
              {errors.owner_postal_code?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.owner_postal_code?.message}</div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label>Pays *</Label>
              <Input
                className={`mt-1.5 ${errors.owner_country?.message ? "border-red-500" : ""}`}
                disabled={isLoading || isOwnerload}
                placeholder="France"
                {...register("owner_country")}
              />
              {errors.owner_country?.message && (
                <div className="text-xs text-red-500 mt-1">{errors.owner_country?.message}</div>
              )}
            </div>
          </div>
        </div>

        {/* Section Informations bancaires */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-6">
            <BanknotesIcon className="w-6 h-6 text-eventToBee mr-3" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Informations bancaires</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Code BIC *</Label>
              <Input
                {...register("bank")}
                disabled={isLoading}
                type="text"
                placeholder="Code BIC"
                className={`mt-1.5 ${errors.bank?.message ? "border-red-500" : ""}`}
              />
              {errors.bank?.message && <div className="text-xs text-red-500 mt-1">{errors.bank?.message}</div>}
            </div>

            <div>
              <Label>IBAN (RIB) *</Label>
              <Input
                {...register("rib")}
                disabled={isLoading}
                type="text"
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                className={`mt-1.5 ${errors.rib?.message ? "border-red-500" : ""}`}
              />
              {errors.rib?.message && <div className="text-xs text-red-500 mt-1">{errors.rib?.message}</div>}
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-blue-500 rounded-full mt-0.5 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                  Informations bancaires sécurisées
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Ces informations sont nécessaires pour recevoir vos paiements. Elles sont stockées de manière
                  sécurisée et chiffrée.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      <OnboardingTour steps={steps} page_name={"CompanyPersonalDataForm"}></OnboardingTour>
    </div>
  );
};

export default LocationCompanyPersonalDataModern;
